import { FC, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  ENQUIRY_TYPE_LABEL,
  Enquiry,
  EnquiryStatus,
  EnquiryUpdate,
} from '@shared/api/enquiry/enquiry.interface';
import { Image } from '@shared/api/media/media.interface';
import { User } from '@shared/api/user/user.interface';
import { Loading } from '@shared/components/Loading';
import {
  fetcher,
  invalidateQueriesByUrlPart,
  queryString,
} from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

type EnquiryPopulated = Enquiry<User, Image>;

//* 답변 LMS 본문에 실리는 답변 글자 상한 (백엔드 enquiry.answered.message.ts와 동일 — 넘으면 "…(이하 생략)")
const ANSWER_SMS_MAX_CHARS = 700;

const STATUS_LABEL: Record<EnquiryStatus, { text: string; color: string }> = {
  [EnquiryStatus.Todo]: { text: '대기', color: 'red' },
  [EnquiryStatus.Doing]: { text: '진행중', color: 'orange' },
  [EnquiryStatus.Done]: { text: '완료', color: 'green' },
};

const AdminEnquiryReplyPage: FC = () => {
  useAdminTitleHook('문의 답변', undefined, undefined, true, '/admin/enquiry');

  const { enquiryId } = useParams<{ enquiryId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  const enquiryUrl = `admin/enquiry/?${queryString({
    filter: { _id: enquiryId },
    populate: [{ path: 'user' }, { path: 'image' }],
  })}`;

  const { data, status } = useQuery<EnquiryPopulated[]>(enquiryUrl, fetcher, {
    enabled: !!enquiryId,
  });

  const enquiry = data?.[0] ?? null;

  const [answer, setAnswer] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const currentAnswer = answer ?? enquiry?.answer ?? '';

  //* 답변 문자 수신번호 — 백엔드와 같은 폴백(문의 폼 번호 → 없으면 계정 번호)
  const smsTargetPhone =
    (enquiry?.phoneNumber || enquiry?.user?.info?.phoneNumber || '').replace(/[^0-9]/g, '');

  const handleSubmit = async (newStatus: EnquiryStatus) => {
    if (!enquiry) return;

    //* 제출(Done)은 접수자에게 문자가 1회 나가고 되돌릴 수 없다 — 한 번 더 확인
    if (newStatus === EnquiryStatus.Done) {
      const target = smsTargetPhone
        ? `${enquiry.name}님(${phoneNumberStringFormator(smsTargetPhone)})에게 답변 문자가 발송됩니다.`
        : '수신 번호가 없어 답변 문자는 발송되지 않습니다.';
      const confirmed = window.confirm(`${target}\n제출할까요? 제출 후에는 답변을 수정할 수 없습니다.`);
      if (!confirmed) return;
    }

    const updateDto: EnquiryUpdate = {
      _id: enquiry._id,
      answer: currentAnswer,
      status: newStatus,
    };

    setSubmitting(true);
    try {
      await api_.patch(`/admin/enquiry/${enquiry._id}`, updateDto);

      const statusText = newStatus === EnquiryStatus.Done ? '제출' : '임시 저장';
      notificateSuccess(notificationInstance, `답변이 ${statusText}되었습니다.`);
      //* 문자열 queryKey는 q.queryKey[0]가 '첫 글자'라 못 잡는다 (헬퍼 주석 참고)
      await invalidateQueriesByUrlPart(queryClient, 'admin/enquiry');

      if (newStatus === EnquiryStatus.Done) {
        navigate('/admin/enquiry');
      }
    } catch (error) {
      notificateError(notificationInstance, '답변 처리 중 오류가 발생했습니다.');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (status !== 'success' || !enquiry) {
    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }

  const label = STATUS_LABEL[enquiry.status];
  const hasImage =
    enquiry.image && typeof enquiry.image === 'object' && enquiry.image.url;
  const isDone = enquiry.status === EnquiryStatus.Done;

  return (
    <div className="space-y-6 px-4 py-4">
      {/* 문의 정보 */}
      <section className="space-y-3 rounded border p-4 theme-border-1">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold">문의 정보</h3>
          <Tag color={label.color}>{label.text}</Tag>
        </div>

        <InfoRow label="이름" value={enquiry.name} />
        <InfoRow label="연락처" value={enquiry.phoneNumber} />
        <InfoRow label="유형" value={ENQUIRY_TYPE_LABEL[enquiry.type] ?? enquiry.type} />
        <InfoRow
          label="문의일"
          value={dayjs(enquiry.createdAt).format('YYYY-MM-DD HH:mm')}
        />
        {enquiry.status !== EnquiryStatus.Todo && enquiry.updatedAt && (
          <InfoRow
            label="최근답변일"
            value={dayjs(enquiry.updatedAt).format('YYYY-MM-DD HH:mm')}
          />
        )}
        {isDone && (
          <InfoRow
            label="답변 문자"
            value={
              enquiry.answerNotifiedAt
                ? `발송 완료 ${dayjs(enquiry.answerNotifiedAt).format('YYYY-MM-DD HH:mm')}`
                : '발송 기록 없음 — 방금 제출했다면 처리 중, 그 외엔 도입(2026-09-04) 전 답변이거나 발송 실패(#프리드-문의-채널 확인)'
            }
          />
        )}
      </section>

      {/* 문의 내용 */}
      <section className="space-y-3 rounded border p-4 theme-border-1">
        <h3 className="text-base font-bold">{enquiry.title}</h3>
        <p className="whitespace-pre-line break-keep text-sm leading-relaxed">
          {enquiry.content}
        </p>
        {hasImage && (
          <div className="pt-2">
            <p className="mb-1 text-xs font-bold theme-text-6">첨부파일</p>
            <a
              href={fileBucketUrl((enquiry.image as Image).url, 'w400')}
              target="_blank"
              rel="noreferrer"
              className="text-xs underline theme-text-main"
            >
              {(enquiry.image as Image).name}
            </a>
          </div>
        )}
      </section>

      {/* 답변 영역 */}
      <section className="space-y-3 rounded border p-4 theme-border-1">
        <h3 className="text-base font-bold">답변</h3>
        <textarea
          value={currentAnswer}
          onChange={(e) => setAnswer(e.currentTarget.value)}
          placeholder="답변을 입력해주세요."
          className="textfield h-40 w-full py-3 text-sm"
          disabled={isDone}
        />
        {!isDone && (
          <div className="space-y-1 text-xs theme-text-6">
            <p>
              <span className="font-bold">답변 제출</span>을 누르면{' '}
              {smsTargetPhone ? (
                <>
                  <span className="font-bold theme-text-1">
                    {phoneNumberStringFormator(smsTargetPhone)}
                  </span>
                  으로 답변 문자가 <span className="font-bold">1회</span> 발송됩니다.
                </>
              ) : (
                <span className="font-bold text-red-500">
                  수신 번호가 없어 문자는 발송되지 않습니다.
                </span>
              )}{' '}
              임시 저장은 발송되지 않고, 제출 후에는 수정·재발송이 없으니 한 번에 완결해 주세요.
            </p>
            <p className={currentAnswer.length > ANSWER_SMS_MAX_CHARS ? 'text-red-500' : ''}>
              {currentAnswer.length.toLocaleString()}자
              {currentAnswer.length > ANSWER_SMS_MAX_CHARS &&
                ` — 문자에는 앞 ${ANSWER_SMS_MAX_CHARS}자만 실리고 "…(이하 생략)"이 붙습니다. 전문은 앱의 내 문의내역에서 볼 수 있습니다.`}
            </p>
          </div>
        )}
      </section>

      {/* 버튼 영역 */}
      {!isDone && (
        <div className="flex flex-col gap-3 pb-8">
          <button
            type="button"
            className="button-rectangle py-3 text-sm font-bold text-white theme-bg-main disabled:opacity-50"
            disabled={submitting || !currentAnswer.trim()}
            onClick={() => handleSubmit(EnquiryStatus.Done)}
          >
            답변 제출 (완료)
          </button>
          <button
            type="button"
            className="button-rectangle border py-3 text-sm font-bold theme-text-main theme-border-1 disabled:opacity-50"
            disabled={submitting}
            onClick={() => handleSubmit(EnquiryStatus.Doing)}
          >
            임시 저장 (진행중)
          </button>
        </div>
      )}

      {isDone && (
        <div className="pb-8">
          <button
            type="button"
            className="button-rectangle border py-3 text-sm font-bold theme-text-1 theme-border-1"
            onClick={() => navigate('/admin/enquiry')}
          >
            목록으로 돌아가기
          </button>
        </div>
      )}
    </div>
  );
};

const InfoRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex text-sm">
    <span className="w-20 shrink-0 font-bold theme-text-6">{label}</span>
    <span className="theme-text-1">{value}</span>
  </div>
);

export default AdminEnquiryReplyPage;
